import { useCallback, useRef } from 'react'
import axios, { AxiosError, AxiosProgressEvent, CancelTokenSource } from 'axios'
import { Endpoint, RoleMimetype } from 'constants/API'
import { VisualFileType } from 'constants/visual'
import { SignedURLDTO, SignedUrlUploadPurchaseSessionRequestBody, UrlDTO } from 'models/visuals'
import { APIRequest } from 'models/API'
import { APIRequestErrorType, APIRequestState } from 'constants/API'

import API from 'utils/API/API'
import { releaseLease, trackLease } from 'components/common/FileAPI/uploadLeases'
import { PurchaseSessionUploadImage, PurchaseSessionDownloadImage, usePurchaseSessionVisualStore } from 'stores/purchaseSessionVisualStore'
import { EntityKeys, QueryType } from 'utils/reactQuery'
import { queryClient } from 'utils/reactQuery/queryInit'
import { logger } from 'utils/logging'

const removeFileExtensionRegex = /\..+/
const DELAY_TIME = 2000
const ITERATION_COUNT = 30
const awaitedErrorCodes = new Set([404, 503])

const waitFor = (millis: number) => new Promise((resolve) => setTimeout(resolve, millis))

export interface UploadPurchaseSessionVisualParams {
  sessionId: string
  productId: number
  productSerial: number
  file: File
  email?: string
  replaces?: string
}

export interface DeletePurchaseSessionVisualParams {
  sessionId: string
  productId: number
  productSerial: number
  filename: string
}

export function usePurchaseSessionVisualOperations() {
  const store = usePurchaseSessionVisualStore()
  const uploadQueueRef = useRef<Promise<void>>(Promise.resolve())

  const downloadVisual = useCallback(async (
    sessionId: string,
    productId: number,
    productSerial: number,
    filename: string,
    type: VisualFileType,
    originalFilename?: string,
    droppedIn?: number
  ) => {
    const url = Endpoint.VISUAL_PURCHASE_SESSION_DOWNLOAD_URL
      .replace('{sessionId}', encodeURIComponent(sessionId))
      .replace('{filename}', encodeURIComponent(filename))
      .replace('{type}', encodeURIComponent(type))

    // Retry loop for signed URL
    let signedUrl: string | undefined
    let lastError: any
    for (let i = 0; i <= ITERATION_COUNT; i++) {
      try {
        const response = await API.post<UrlDTO>(url, {}, {
          headers: { Accept: RoleMimetype.CLIENT },
        }, {
          endpoint: Endpoint.VISUAL_PURCHASE_SESSION_DOWNLOAD_URL,
          ignoreErrorFactory: (error: AxiosError) => awaitedErrorCodes.has(error.response?.status || -1),
        })
        if (response.status === 503) {
          await waitFor(DELAY_TIME)
          continue
        }
        signedUrl = response.data?.url
        break
      } catch (error) {
        lastError = error
        const axiosErr = error as AxiosError
        if (awaitedErrorCodes.has(axiosErr.response?.status || -1)) {
          if (i < ITERATION_COUNT) {
            await waitFor(DELAY_TIME)
            continue
          }
        }
        break
      }
    }

    if (!signedUrl) {
      const downloadData: PurchaseSessionDownloadImage = {
        file: new File([], filename),
        type,
        originalFilename,
        request: APIRequest.factoryError(APIRequestErrorType.NOTFOUND_ERROR, 'Visual not found') as any,
        lastUpdated: Date.now(),
      }
      store.handleDownloadReceived(sessionId, productId, productSerial, filename, type, downloadData)
      return
    }

    // Verify signed URL is accessible
    for (let i = 0; i < ITERATION_COUNT; i++) {
      try {
        await API.get<unknown>(signedUrl, undefined, {
          endpoint: 'VISUAL_SIGNED_URL',
          ignoreBaseUrl: true,
          ignoreAuthorizationHeaders: true,
          ignoreErrorFactory: (error: AxiosError) => awaitedErrorCodes.has(error.response?.status || -1),
        })

        const downloadData: PurchaseSessionDownloadImage = {
          file: new File([], filename),
          type,
          signedUrl,
          originalFilename,
          request: APIRequest.factoryOK(null, null as any) as any,
          lastUpdated: Date.now(),
        }
        store.handleDownloadReceived(sessionId, productId, productSerial, filename, type, downloadData)
        return
      } catch (error) {
        const axiosErr = error as AxiosError
        if (!axiosErr.response || !awaitedErrorCodes.has(axiosErr.response.status)) {
          // Non-retryable error - store the result with signedUrl
          const downloadData: PurchaseSessionDownloadImage = {
            file: new File([], filename),
            type,
            signedUrl,
            originalFilename,
            request: APIRequest.factoryOK(null, null as any) as any,
            lastUpdated: Date.now(),
          }
          store.handleDownloadReceived(sessionId, productId, productSerial, filename, type, downloadData)
          return
        }
      }
      await waitFor(DELAY_TIME)
    }

    // Exhausted retries
    const downloadData: PurchaseSessionDownloadImage = {
      file: new File([], filename),
      type,
      originalFilename,
      request: APIRequest.factoryError(APIRequestErrorType.NOTFOUND_ERROR, 'Visual not found') as any,
      lastUpdated: Date.now(),
    }
    store.handleDownloadReceived(sessionId, productId, productSerial, filename, type, downloadData)
  }, [store])

  /** Pending batch of upload registrations — flushed via queueMicrotask for a single Zustand set() */
  const pendingBatchRef = useRef<Array<{ sessionId: string, productId: number, productSerial: number, fileName: string, type: VisualFileType, data: PurchaseSessionUploadImage, originalFilename: string, replaces?: string }>>([])
  const batchScheduledRef = useRef(false)

  const registerUpload = useCallback((params: UploadPurchaseSessionVisualParams) => {
    const { sessionId, productId, productSerial, file, replaces } = params
    const source = axios.CancelToken.source()
    const isReplacement = !!replaces

    const initialData: PurchaseSessionUploadImage = {
      file,
      type: VisualFileType.RAW,
      progress: 0,
      cancelToken: source,
      request: new APIRequest() as APIRequest<null>,
      droppedIn: Date.now(),
      originalFilename: file.name,
      replaces,
    }

    const fileName = isReplacement ? replaces! : file.name
    pendingBatchRef.current.push({ sessionId, productId, productSerial, fileName, type: VisualFileType.RAW, data: initialData, originalFilename: file.name, replaces })

    if (!batchScheduledRef.current) {
      batchScheduledRef.current = true
      queueMicrotask(() => {
        const batch = pendingBatchRef.current
        pendingBatchRef.current = []
        batchScheduledRef.current = false
        store.handleBatchUploadStart(batch)
      })
    }

    return { source, initialData }
  }, [store])

  const uploadVisual = useCallback(async (params: UploadPurchaseSessionVisualParams, registered: { source: CancelTokenSource, initialData: PurchaseSessionUploadImage }) => {
    const { sessionId, productId, productSerial, file, email, replaces } = params
    const isReplacement = !!replaces
    const { source, initialData } = registered

    const fileName = isReplacement ? replaces! : file.name

    // Step 1: Get signed URL
    const dataForSignedUrl: SignedUrlUploadPurchaseSessionRequestBody = {
      email,
      productId,
      visualMetadata: {
        filename: isReplacement ? replaces! : file.name,
        contentType: file.type,
        type: VisualFileType.RAW,
        ...(isReplacement && { replacementFilename: file.name }),
      },
    }

    const endpoint = isReplacement
      ? Endpoint.VISUAL_PURCHASE_SESSION_UPLOAD_URL_REPLACE
      : Endpoint.VISUAL_PURCHASE_SESSION_UPLOAD_URL
    const method = isReplacement ? 'put' : 'post'
    const url = endpoint.replace('{sessionId}', encodeURI(sessionId))

    let signedUrlDTO: SignedURLDTO
    try {
      const response = await API[method]<SignedURLDTO>(url, dataForSignedUrl, {}, {
        endpoint,
      })
      signedUrlDTO = response.data
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || error.message
        : 'Failed to get upload URL'
      logger.error('Purchase session upload signed URL failed', error, { context: { component: 'usePurchaseSessionVisualOperations', action: 'getSignedUrl' } })
      store.setUploadError(sessionId, productId, productSerial, fileName, VisualFileType.RAW, errorMessage)
      return
    }

    if (!signedUrlDTO?.signedURL) {
      // The mint committed a placeholder before it came back unusable — release it now rather than waiting out the reaper
      void releaseLease(signedUrlDTO?.leaseId)
      logger.error('signedUrl is undefined, null or empty')
      store.setUploadError(sessionId, productId, productSerial, fileName, VisualFileType.RAW, 'signedUrl is undefined, null or empty')
      return
    }

    // The mint carries no cancel token, so a tile deleted while it was in flight must not be resurrected by the rename below
    if (source.token.reason) {
      void releaseLease(signedUrlDTO.leaseId)
      return
    }

    // Parse filename from signed URL
    const split = signedUrlDTO.signedURL.split('?')[0].split('/')
    const resolvedFilename = split[split.length - 1].replace(removeFileExtensionRegex, '')

    // Rename entry from local filename to server-assigned filename
    if (resolvedFilename !== fileName) {
      store.handleUploadStart(
        sessionId, productId, productSerial, resolvedFilename, VisualFileType.RAW,
        { ...initialData, file: new File([], resolvedFilename), signedUrl: signedUrlDTO.signedURL, cancelToken: source, originalFilename: file.name },
        fileName, replaces
      )
    }

    // Step 2: Upload with progress (standard only for purchase sessions)
    // This path does not go through the shared uploader wrappers, so it keeps its own lease alive
    trackLease(signedUrlDTO.leaseId)
    try {
      await axios.put(signedUrlDTO.signedURL, file, {
        cancelToken: source.token,
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1))
          store.setUploadProgress(sessionId, productId, productSerial, resolvedFilename, VisualFileType.RAW, {
            ...initialData,
            progress: percent,
            file: new File([], resolvedFilename),
            signedUrl: signedUrlDTO.signedURL,
            cancelToken: source,
            originalFilename: file.name,
            request: new APIRequest(APIRequestState.RUNNING) as APIRequest<null>,
          })
        },
        headers: {
          'Content-type': file.type,
          ...signedUrlDTO.headers,
        },
      })
    } catch (error) {
      if (axios.isCancel(error)) return
      const errorMessage = error instanceof Error ? error.message : 'Upload failed'
      logger.error('Purchase session visual upload failed', error, { context: { component: 'usePurchaseSessionVisualOperations', action: 'upload' } })
      store.setUploadError(sessionId, productId, productSerial, resolvedFilename, VisualFileType.RAW, errorMessage)
      return
    } finally {
      void releaseLease(signedUrlDTO.leaseId)
    }

    // Upload complete
    store.setUploadComplete(sessionId, productId, productSerial, resolvedFilename, VisualFileType.RAW, {
      file: new File([], resolvedFilename),
      signedUrl: signedUrlDTO.signedURL,
      cancelToken: source,
      originalFilename: file.name,
    })

    // Step 3: Trigger THUMB + WEB download
    const delayMs = isReplacement ? 10000 : 0
    const triggerDownloads = async () => {
      await downloadVisual(sessionId, productId, productSerial, resolvedFilename, VisualFileType.RAW_THUMB, file.name, Date.now())
      await downloadVisual(sessionId, productId, productSerial, resolvedFilename, VisualFileType.RAW_WEB, file.name, Date.now())
    }

    if (delayMs > 0) {
      setTimeout(triggerDownloads, delayMs)
    } else {
      await triggerDownloads()
    }
  }, [store, downloadVisual])

  const deleteVisual = useCallback(async (params: DeletePurchaseSessionVisualParams) => {
    const { sessionId, productId, productSerial, filename } = params

    // Cancel ongoing upload
    const cancelToken = store.getUploadCancelToken(sessionId, productId, productSerial, filename, VisualFileType.RAW)
    if (cancelToken) cancelToken.cancel()

    // Mark as deleting
    store.startDelete(sessionId, productId, productSerial, filename, VisualFileType.RAW)

    // API call
    const url = Endpoint.VISUAL_PURCHASE_SESSION_DELETE
      .replace('{sessionId}', encodeURIComponent(sessionId))
      .replace('{filename}', encodeURIComponent(filename))

    try {
      await API.delete<null>(url, {
        headers: { Accept: RoleMimetype.CLIENT },
      }, {
        endpoint: Endpoint.VISUAL_PURCHASE_SESSION_DELETE,
      })
    } catch (error) {
      logger.error('Purchase session visual delete failed', error, { context: { component: 'usePurchaseSessionVisualOperations', action: 'delete' } })
    }

    // Complete delete
    store.completeDelete(sessionId, productId, productSerial, filename, VisualFileType.RAW)

    // Invalidate React Query cache
    queryClient.invalidateQueries({ queryKey: [EntityKeys.PURCHASE_SESSION_VISUAL, QueryType.LIST, { sessionId }] })
  }, [store])

  /** Queue uploads sequentially (matches original saga behavior).
   *  Store entries are written immediately so all cards appear at once. */
  const queueUpload = useCallback((params: UploadPurchaseSessionVisualParams) => {
    const registered = registerUpload(params)
    uploadQueueRef.current = uploadQueueRef.current.then(() => uploadVisual(params, registered)).catch(() => { })
  }, [registerUpload, uploadVisual])

  return {
    uploadVisual: queueUpload,
    uploadVisualReplacement: (params: UploadPurchaseSessionVisualParams) => queueUpload(params),
    downloadVisual,
    deleteVisual,
  }
}
